import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";

import { IcoPlus, TitleH1 } from "components/generic";
import {
  ContainerValue,
  ContainerView,
  CorrectPullRefresh,
  PullGrid
} from "components/containers";
import { MainWrapper } from "pages/HostHouseRegistration/styles";
import { HOST_HOUSE_WIZARD_ROUTE } from "utils/constants";
import { RoomCard } from "./RoomCard";
import { LoaderCustom } from "components/Loader";
import { theme } from "styles/theme";
import {
  invalidateHouses,
  useCreateWizard,
  useDeleteWizard,
  useGetHostHouses,
  useGetWizardsStateShort
} from "../../API/queries/houseApiQueries";
import { Button, Skeleton } from "antd";
import styled from "styled-components/macro";
import { WrapperContainer } from "./RoomCard/styles";
import { useNotifier } from "../../context/ContextNotifier";
import { useContextHospi } from "../../context/ContextHospi";

const NotFinishedRoom = styled.div`
  display: flex;
  justify-content: space-between;
`;
const FinishedRoomButtons = styled.div`
  display: flex;
  gap: 16px;
`;

export const HostRooms: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("hostrooms");
  const { cognitoUser } = useContextHospi();

  const hostHouses = useGetHostHouses();
  const houses = hostHouses.data;

  const createWizard = useCreateWizard();
  const deleteWizard = useDeleteWizard();
  const { data: wizards, isLoading: isLoadingWizards } =
    useGetWizardsStateShort();

  const notifier = useNotifier();

  const handleAddNewRoom = () => {
    if (!houses) {
      return;
    }

    createWizard.mutate(
      {
        type: houses.length === 0 ? "description" : "full"
      },
      {
        onSuccess: (wizard) => {
          navigate(
            generatePath(HOST_HOUSE_WIZARD_ROUTE, {
              houseId: wizard.house_id,
              wizardType: wizard.wizard_type,
              lng: i18n.language
            })
          );
        }
      }
    );
  };

  const notAllowedToAddNewRoom =
    !wizards ||
    wizards.some(
      (w) => w.wizard_type === "description" && !w.metadata.finished
    ) ||
    wizards.some((w) => w.wizard_type === "publish" && !w.metadata.finished);

  return (
    <ContainerView>
      <MainWrapper>
        {hostHouses.isFetching && (
          <LoaderCustom size={5} color={theme.colors.bgBody} />
        )}
        <CorrectPullRefresh>
          <PullToRefresh
            onRefresh={async () => {
              if (cognitoUser?.id) {
                invalidateHouses(cognitoUser.id);
              }
            }}
            pullingContent={
              <LoaderCustom
                size={5}
                color={theme.colors.bgBody}
                text={t("textLoading.3")}
              />
            }
            className="pullRefresh  "
          >
            <PullGrid>
              <TitleH1>{t("dashboard.title")}</TitleH1>
              {houses ? (
                <>
                  {houses
                    .filter((item) => item.house_status !== "ARCHIVED")
                    .map((room) => (
                      <RoomCard
                        house={
                          room.house_status === "RENTED"
                            ? { ...room, published_at: "" }
                            : room
                        }
                        key={room.house_id}
                      />
                    ))}

                  {!notAllowedToAddNewRoom ? (
                    <Button
                      onClick={handleAddNewRoom}
                      loading={createWizard.isLoading}
                      icon={<IcoPlus size="1.5em" />}
                      size={"large"}
                    >
                      {" " + t("dashboard.emptyArray.buttonAdditionalRoom")}
                    </Button>
                  ) : null}
                </>
              ) : null}
              {isLoadingWizards ? <Skeleton.Button block active /> : null}
              {wizards
                ?.filter((w) => !w.metadata.finished)
                .map((w) => (
                  <ContainerValue key={w.house_id}>
                    <WrapperContainer>
                      <NotFinishedRoom>
                        <div>{t("dashboard.emptyArray.notFinishedRoom")} </div>
                        <FinishedRoomButtons>
                          <Button
                            onClick={() => {
                              navigate(
                                generatePath(HOST_HOUSE_WIZARD_ROUTE, {
                                  houseId: w.house_id,
                                  wizardType: w.wizard_type,
                                  lng: i18n.language
                                })
                              );
                            }}
                          >
                            {t("dashboard.emptyArray.finishCreateRoom")}
                          </Button>
                          {w.wizard_type === "full" ? (
                            <Button
                              onClick={() => {
                                deleteWizard.mutate(
                                  {
                                    type: w.wizard_type,
                                    houseId: w.house_id
                                  },
                                  {
                                    onSuccess: () => {
                                      notifier.success({
                                        message: t(
                                          "dashboard.emptyArray.unfinishedPopup"
                                        )
                                      });
                                    }
                                  }
                                );
                              }}
                              loading={
                                deleteWizard.isLoading &&
                                deleteWizard.variables?.houseId === w.house_id
                              }
                              danger={true}
                            >
                              {t(
                                "dashboard.emptyArray.removeUnfinishedHouseWizard"
                              )}
                            </Button>
                          ) : null}
                        </FinishedRoomButtons>
                      </NotFinishedRoom>
                    </WrapperContainer>
                  </ContainerValue>
                ))}
            </PullGrid>
          </PullToRefresh>
        </CorrectPullRefresh>
      </MainWrapper>
    </ContainerView>
  );
};
